<template>
  <nav class="router-link">
    <!-- FIXED BAR -->
    <Container
      use-full-width
      class="fixed inset-x-0 top-0 z-30 flex h-navbar-height items-center justify-between bg-white pb-3 pt-4"
    >
      <!-- LOGO -->
      <Transition name="fade" mode="out-in">
        <NuxtLink
          v-if="!isNavOpen"
          key="logo"
          :to="localePath('/')"
          :aria-label="$t('aria_labels.home')"
        >
          <IconWetrokLogo class="h-12" />
        </NuxtLink>

        <button
          v-else-if="showBackButton"
          key="backButton"
          type="button"
          class="flex shrink-0 -translate-x-2 items-center whitespace-nowrap"
          :aria-label="$t('Gehe eine Navigationsebene zurück')"
          @click="leave()"
        >
          <IconChevron class="-mt-1 w-6 shrink-0 rotate-90" />
          <span class="ml-1 typo-subtitle">{{ $t('back') }}</span>
        </button>
      </Transition>

      <BurgerButton
        class="ml-auto flex h-4 w-7 items-center"
        :aria-label="$t('aria_labels.toggle_navigation')"
        :open="isNavOpen"
        @click="toggleNav"
      />
    </Container>

    <!-- NAVIGATION -->
    <div
      class="
        fixed inset-x-0 top-0 overflow-hidden bg-white shadow-100 transition-[height] duration-500 ease-in-out
      "
      :class="isNavOpen ? 'h-svh':'h-0'"
    >
      <div
        class="h-svh max-h-svh w-[200vw] overflow-y-auto pt-navbar-height"
        :class="{ 'opacity-0 transition-opacity duration-500': !isNavOpen }"
      >
        <div
          ref="navContentElement"
          class="
            relative w-[200vw] overflow-hidden transition-all duration-500 ease-in-out will-change-transform
          "
          :class="translation"
        >
          <ul>
            <!-- MAIN LEVEL 1 -->
            <li
              v-for="item in main"
              :key="item.page.id"
            >
              <Container class="w-1/2" use-full-width>
                <div class="flex items-center justify-between gap-3 border-b border-grey-200 py-4">
                  <NuxtLink
                    :to="item.page.url"
                    class="font-semibold transition-colors typo-subtitle hover:text-primary-900"
                  >
                    {{ item.page.title }}
                  </NuxtLink>
                  <button
                    v-if="item.children?.length"
                    class="pl-5"
                    :aria-label="`${$t('aria_labels.show_sub_nav_elements')} ${item.page.title}`"
                    type="button"
                    @click="enter(item)"
                  >
                    <IconChevron class="w-7 shrink-0 -rotate-90" />
                  </button>
                </div>
              </Container>

              <!-- SUB NAVIGATION -->
              <SubNavigationMobile
                v-show="item === selectedNavItem"
                :show="item === selectedNavItem"
                :sub-navigation="item.children"
                class="absolute inset-y-0 left-1/2 right-0"
                @height-change="setHeightOfNavContent($event)"
              />
            </li>
          </ul>
        </div>

        <!-- META -->
        <Container class="mt-9 w-screen pb-18" use-full-width>
          <ul class="space-y-4">
            <li v-for="item in meta" :key="item.page.id">
              <NuxtLink
                :to="item.page.url"
                class="text-grey-300 transition-colors typo-subtitle hover:text-primary-900"
              >
                {{ item.page.title }}
              </NuxtLink>
            </li>

            <li class="pt-4">
              <LangSwitcherMobile />
            </li>
          </ul>
        </Container>
      </div>
    </div>
  </nav>
</template>

<!-- eslint-disable-next-line vue/block-lang -->
<script setup>
import { useScrollLock } from '@vueuse/core'

const globals = inject(GlobalsKey, null)
const categories = inject('categoriesTree', ref([]))

const main = computed(() => {
  const mainNav = globals?.value.main?.tree || []

  // Find the on the first level the page with property is_categories_container = true and set its children to categories
  const categoriesContainer = mainNav.find(item => item.page.is_categories_container)
  if (categoriesContainer) {
    categoriesContainer.children = categories.value
  }

  return mainNav
})

const meta = computed(() => globals?.value.meta?.tree)

const route = useRoute()

const navContentElement = ref(null)
const initialNavContentHeight = ref(0)

const selectedNavItem = ref(null)
const showBackButton = ref(false)

const isNavOpen = ref(false)
const translation = ref('translate-0')

const body = ref(null)
let isLocked

function toggleNav() {
  isNavOpen.value = !isNavOpen.value
  isLocked.value = isNavOpen.value
}

async function enter(item) {
  selectedNavItem.value = item
  translation.value = '-translate-x-1/2'
  showBackButton.value = true
}

async function leave() {
  translation.value = 'translate-0'
  showBackButton.value = false
  setHeightOfNavContent(initialNavContentHeight.value)
  await new Promise(resolve => setTimeout(resolve, 500))
  selectedNavItem.value = null
}

watch(
  () => route.path,
  () => {
    if (isNavOpen.value) {
      toggleNav()
    }
  },
)

function setHeightOfNavContent(height) {
  navContentElement.value.style.height = `${height}px`
}

onMounted(() => {
  // Since document.body is available only client-side, assign it here
  body.value = document.body
  isLocked = useScrollLock(body)
  initialNavContentHeight.value = navContentElement.value.offsetHeight
})

onUnmounted(() => {
  // Ensure the body is unlocked when the component is unmounted
  if (isLocked) {
    isLocked.value = false
  }
})
</script>

<style scoped>
.box-shadow-top {
  box-shadow: 0px -1px 10px 0px #22242b;
}
.width-enter-active,
.width-leave-active {
  transition-property: opacity, width;
  @apply duration-300 ease-out;
}

.width-enter-from,
.width-leave-to {
  @apply w-0 opacity-0;
}

.left-in-enter-active,
.left-in-leave-active {
  transition-property: opacity, transform;
  @apply duration-300 ease-out;
}

.left-in-enter-from,
.left-in-leave-to {
  @apply -translate-x-full opacity-0;
}
</style>

<style>
.delay-enter-active,
.delay-leave-active {
  transition-delay: 0.3s;
}
</style>
